import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, TriangleAlert } from 'lucide-react';

import { useElementTypes } from '@/features/elements';
import { Button } from '@/shared/ui';

import type { ImportConflictDetail, ImportConflictItem } from '../model/types';
import styles from './Import.module.css';

interface Props {
  item: ImportConflictItem;
  detail: ImportConflictDetail;
  readOnly: boolean;
  busy: boolean;
  onChoose: (choice: string) => void;

  /**
   * Mapowanie typu.
   *
   * ⚠ Osobne wejście, a nie kolejny `choice`, bo to jest operacja o innym zasięgu:
   * rozstrzygnięcie dotyczy JEDNEGO wiersza, a mapowanie typu — <b>wszystkich wierszy
   * o tej wartości w całym przebiegu</b>. Przemycenie go w tym samym kanale znaczyłoby,
   * że jedno kliknięcie robi raz jedną rzecz, a raz sześćdziesiąt jeden.
   */
  onMapType: (targetType: string) => void;
}

/**
 * Rozwinięcie jednej pozycji — <b>fakty po lewej, decyzja po prawej</b>.
 *
 * ⭐ Każdy rodzaj pokazuje <b>to, czego liczba nie powie</b>: przy przeprowadzce — ile bytów
 * pojedzie razem i czy stary rodzic zostanie pusty; przy podejrzeniu duplikatu — kto ostatnio
 * ruszył kandydata; przy różnicy wartości — <b>które</b> pola się różnią i czy różnica jest
 * wyłącznie w wielkości liter.
 */
export function ConflictDetail({ item, detail, readOnly, busy, onChoose, onMapType }: Props) {
  const { t } = useTranslation();

  return (
    <section className={styles['detail']} aria-label={t(`import.kind.${detail.kind}`)}>
      <div className={styles['detailFacts']}>
        <Facts item={item} detail={detail} />
      </div>

      {readOnly ? null : (
        <div className={styles['detailChoices']}>
          <Choices detail={detail} busy={busy} onChoose={onChoose} onMapType={onMapType} />
        </div>
      )}
    </section>
  );
}

function Facts({ item, detail }: { item: ImportConflictItem; detail: ImportConflictDetail }) {
  const { t, i18n } = useTranslation();
  const format = new Intl.NumberFormat(i18n.language);

  if (detail.valueDiffers) {
    return (
      <dl className={styles['diff']}>
        {detail.valueDiffers.fields.map((field) => (
          <div key={field.field} className={styles['diffRow']}>
            <dt className={styles['diffField']}>
              {t(`import.field.${field.field}`, { defaultValue: field.field })}
              {field.caseOnly ? (
                <span className={styles['chip']}>{t('import.detail.caseOnly')}</span>
              ) : null}
            </dt>
            <dd className={styles['diffValues']}>
              <span className={styles['valueOld']}>{field.dbValue ?? '—'}</span>
              <ArrowRight size={14} aria-hidden="true" className={styles['diffArrow']} />
              <span className={styles['valueNew']}>{field.fileValue ?? '—'}</span>
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  if (detail.duplicateSuspect) {
    return (
      <div className={styles['facts']}>
        <p className={styles['factLead']}>
          {t('import.detail.duplicateLead', { title: detail.duplicateSuspect.candidateTitle })}
        </p>
        {/* ⚠ Najcięższy rodzaj (decyzja 99): błędne sklejenie dwóch bytów jest „prawie nie
            do rozdzielenia". Dlatego mówimy wprost, czym to grozi. */}
        <p className={styles['factWarn']}>
          <TriangleAlert size={14} aria-hidden="true" />
          {t('import.detail.duplicateWarn')}
        </p>
      </div>
    );
  }

  if (detail.reparent) {
    const { currentParentTitle, fileParentTitle, subtreeCount, currentParentBecomesEmpty } =
      detail.reparent;

    return (
      <div className={styles['facts']}>
        <p className={styles['move']}>
          <span className={styles['valueOld']}>{currentParentTitle ?? t('import.detail.noParent')}</span>
          <ArrowRight size={14} aria-hidden="true" className={styles['diffArrow']} />
          <span className={styles['valueNew']}>{fileParentTitle ?? t('import.detail.newParent')}</span>
        </p>

        {subtreeCount > 0 ? (
          <p className={styles['factLead']}>
            {t('import.detail.subtree', { count: subtreeCount })}
          </p>
        ) : null}

        {currentParentBecomesEmpty ? (
          // ⭐ Rzecz, której liczba nie powie: po przeprowadzce dotychczasowy rodzic zostaje
          // pusty — czyli najpewniej trzeba poprawić generator, a nie klikać „przenieś".
          <p className={styles['factWarn']}>
            <TriangleAlert size={14} aria-hidden="true" />
            {t('import.detail.parentBecomesEmpty')}
          </p>
        ) : null}
      </div>
    );
  }

  if (detail.deletedReturns) {
    return (
      <div className={styles['facts']}>
        <p className={styles['factLead']}>
          {t('import.detail.deleted', {
            date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
              new Date(detail.deletedReturns.deletedAtUtc),
            ),
          })}
        </p>
        <p className={styles['factHint']}>
          {t('import.detail.deletedRelations', { count: detail.deletedReturns.relationCount })}
        </p>
      </div>
    );
  }

  if (detail.unknownValue) {
    return (
      <div className={styles['facts']}>
        <p className={styles['factLead']}>
          <code className={styles['code']}>{detail.unknownValue.fileValue}</code>
        </p>
        {/* ⭐ Liczba wystąpień jest warunkiem wykonalności mapowania hurtowego: mówi adminowi,
            że jedno kliknięcie załatwia dziesiątki pozycji. */}
        <p className={styles['factHint']}>
          {t('import.detail.occurrences', { count: detail.unknownValue.occurrences })}
        </p>
      </div>
    );
  }

  if (detail.danglingRef) {
    return (
      <div className={styles['facts']}>
        <p className={styles['factLead']}>
          <code className={styles['code']}>
            {detail.danglingRef.missingSource}:{detail.danglingRef.missingKey}
          </code>
        </p>
        <p className={styles['factHint']}>{t('import.detail.dangling')}</p>
      </div>
    );
  }

  return <p className={styles['factHint']}>{format.format(item.id)}</p>;
}

function Choices({
  detail,
  busy,
  onChoose,
  onMapType,
}: {
  detail: ImportConflictDetail;
  busy: boolean;
  onChoose: (choice: string) => void;
  onMapType: (targetType: string) => void;
}) {
  const { t } = useTranslation();

  if (detail.kind === 'unknownType') {
    return (
      <TypeMapping
        busy={busy}
        chosen={detail.choice ?? null}
        onMapType={onMapType}
        onSkip={() => onChoose('skip')}
      />
    );
  }

  const choices: Record<string, string[]> = {
    duplicateSuspect: ['adopt_keep_title', 'adopt_take_title', 'create_separate', 'skip'],
    reparent: ['move', 'leave', 'skip'],
    deletedReturns: ['restore', 'leave_deleted'],
    valueDiffers: ['take_file', 'keep_db', 'skip'],
    // ⛔ Konto ZABLOKOWANE nie dostaje „przypisz mimo to": takiego konta nie wolno nigdzie
    //    użyć, a plik nie unieważnia decyzji admina. ⭐ Sedno tego konfliktu brzmi „Jan odszedł,
    //    kto przejmuje?", więc pierwszym wyjściem jest wskazanie następcy (mapowanie w grupie).
    disabledOwner: ['leave_no_owner', 'skip'],

    // ⚠ Konto CZEKAJĄCE wolno podpiąć — ono po prostu jeszcze nie weszło.
    inactiveOwner: ['assign', 'leave_no_owner', 'skip'],
    danglingRef: ['skip'],
  };

  const list = choices[detail.kind] ?? [];

  return (
    <>
      {list.map((choice, index) => {
        // ⭐⭐ Wybór już zapisany musi być WIDOCZNY, bo pozycja z dwoma rodzajami zostaje
        // w stanie „czeka" także po rozstrzygnięciu jednego z nich. Bez tego kliknięcie
        // wyglądało jak brak reakcji — znalezione na żywym ekranie.
        const chosen = detail.choice === choice;

        return (
          <Button
            key={choice}
            // ⚠ Pierwszy wybór NIE jest „głównym" wszędzie: przy podejrzeniu duplikatu
            // najbezpieczniejsze jest utworzenie osobno, a nie sklejenie.
            variant={chosen ? 'primary' : index === 0 && detail.kind !== 'duplicateSuspect' ? 'primary' : 'default'}
            aria-pressed={chosen}
            disabled={busy}
            onClick={() => onChoose(choice)}
          >
            {chosen ? <Check size={14} aria-hidden="true" /> : null}
            {t(`import.choice.${detail.kind}.${choice}`)}
          </Button>
        );
      })}
    </>
  );
}

/**
 * Mapowanie typu spoza słownika.
 *
 * ⛔ <b>Wyłącznie na typ istniejący</b> (decyzja 87) — z tego ekranu nie da się dopisać wpisu
 * do słownika, bo słownik dopisywany z pliku przestaje być kontrolowany po jednym przebiegu.
 */
function TypeMapping({
  busy,
  chosen,
  onMapType,
  onSkip,
}: {
  busy: boolean;
  chosen: string | null;
  onMapType: (targetType: string) => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const types = useElementTypes(true);
  const [target, setTarget] = useState('');

  return (
    <div className={styles['mapping']}>
      <label className={styles['mappingLabel']}>
        {t('import.detail.mapTo')}
        <select
          className={styles['select']}
          value={target}
          disabled={busy || types.isPending}
          onChange={(event) => setTarget(event.target.value)}
        >
          <option value="">{t('import.detail.pickType')}</option>
          {(types.data ?? []).map((type) => (
            <option key={type.id} value={type.name}>
              {type.name}
            </option>
          ))}
        </select>
      </label>

      <Button
        variant="primary"
        disabled={busy || target === ''}
        onClick={() => onMapType(target)}
      >
        {t('import.detail.map')}
      </Button>

      <Button
        variant={chosen === 'skip' ? 'primary' : 'default'}
        aria-pressed={chosen === 'skip'}
        disabled={busy}
        onClick={onSkip}
      >
        {chosen === 'skip' ? <Check size={14} aria-hidden="true" /> : null}
        {t('import.choice.unknownType.skip')}
      </Button>
    </div>
  );
}
